import { apiBaseUrl, auth, fetchAppCheckToken } from '../firebase';
import { onAuthStateChanged, type User } from '@firebase/auth';
import { handleBlockedAccount } from './accountStatus';

let authReady: Promise<User | null> | null = null;

/** Resolves once Firebase has restored the signed-in session (currentUser is null until then after a page load). */
export function waitForAuthUser(): Promise<User | null> {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  if (!authReady) {
    authReady = new Promise((resolve) => {
      const timer = setTimeout(() => resolve(auth.currentUser), 10_000);
      const unsub = onAuthStateChanged(auth, (u) => {
        clearTimeout(timer);
        unsub();
        resolve(u);
      });
    });
  }
  return authReady.then((u) => auth.currentUser || u);
}

interface ErrorResponse {
  error?: string;
  code?: string;
  details?: unknown;
}

async function callSecureApi<T>(endpoint: string, init?: RequestInit): Promise<T> {
  const user = auth.currentUser || await waitForAuthUser();
  if (!user) {
    throw new Error('auth-required');
  }

  if (!apiBaseUrl) {
    throw new Error('api-base-not-configured');
  }

  const [idToken, appCheckToken] = await Promise.all([
    user.getIdToken(),
    fetchAppCheckToken().catch(() => null),
  ]);

  const normalizedBase = apiBaseUrl.replace(/\/$/, '');
  const mergedHeaders = new Headers({
    Authorization: `Bearer ${idToken}`,
  });
  if (appCheckToken) {
    mergedHeaders.set('X-Firebase-AppCheck', appCheckToken);
  }
  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => mergedHeaders.set(key, value));
  }

  const response = await fetch(`${normalizedBase}/${endpoint}`, {
    ...init,
    method: init?.method || 'GET',
    headers: mergedHeaders,
  });

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const err = data as ErrorResponse | null;
    if (response.status === 401) handleBlockedAccount(err?.code || err?.error);
    const message = err?.error || err?.code || response.statusText;
    const error = new Error(message);
    (error as any).code = err?.code || response.status;
    (error as any).details = err?.details;
    throw error;
  }
  return data as T;
}

async function callPublicApi<T>(endpoint: string, init?: RequestInit): Promise<T> {
  if (!apiBaseUrl) {
    throw new Error('api-base-not-configured');
  }

  const normalizedBase = apiBaseUrl.replace(/\/$/, '');
  const response = await fetch(`${normalizedBase}/${endpoint}`, {
    method: init?.method || 'GET',
    headers: init?.headers,
    body: init?.body,
    ...init,
  });

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const err = data as ErrorResponse | null;
    if (response.status === 401) handleBlockedAccount(err?.code || err?.error);
    const message = err?.error || err?.code || response.statusText;
    const error = new Error(message);
    (error as any).code = err?.code || response.status;
    (error as any).details = err?.details;
    throw error;
  }
  return data as T;
}

async function callSecureJson<T>(endpoint: string, payload: unknown, init?: RequestInit): Promise<T> {
  const mergedHeaders = new Headers({
    'Content-Type': 'application/json',
  });
  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => mergedHeaders.set(key, value));
  }
  return callSecureApi<T>(endpoint, {
    method: init?.method || 'POST',
    headers: mergedHeaders,
    body: JSON.stringify(payload ?? {}),
    ...init,
  });
}

async function callSecureFunction<T>(endpoint: string, payload: unknown, init?: RequestInit): Promise<T> {
  const user = auth.currentUser || await waitForAuthUser();
  if (!user) {
    throw new Error('auth-required');
  }

  if (!apiBaseUrl) {
    throw new Error('api-base-not-configured');
  }

  const [idToken, appCheckToken] = await Promise.all([
    user.getIdToken(),
    fetchAppCheckToken().catch(() => null),
  ]);

  const normalizedBase = apiBaseUrl.replace(/\/$/, '');
  const mergedHeaders = new Headers({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
  });
  if (appCheckToken) {
    mergedHeaders.set('X-Firebase-AppCheck', appCheckToken);
  }
  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => mergedHeaders.set(key, value));
  }

  const response = await fetch(`${normalizedBase}/${endpoint}`, {
    method: 'POST',
    headers: mergedHeaders,
    body: JSON.stringify(payload ?? {}),
    ...init,
  });

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const err = data as ErrorResponse | null;
    if (response.status === 401) handleBlockedAccount(err?.code || err?.error);
    const message = err?.error || err?.code || response.statusText;
    const error = new Error(message);
    (error as any).code = err?.code || response.status;
    (error as any).details = err?.details;
    throw error;
  }
  return data as T;
}

export interface ModerationAttestResponse {
  moderationToken: string;
  claims: Record<string, unknown>;
  expiresAt: number;
  nonce: string;
  needsReview?: boolean;
}

export function submitModerationAppeal(payload: {
  contentType: 'post' | 'pulse' | 'testimony' | 'upload';
  contentId: string;
  reason: string;
}) {
  return callSecureFunction<{ ok: true; appealId: string; status: string }>('moderationAppeal', payload);
}

export function attestModeration(payload: {
  contentPath: string;
  contentType?: string;
  textSamples?: string[];
  // Legacy fields (ignored by VPS, but kept for compatibility)
  nsfwScore?: number;
  deviceCompatible?: boolean;
  evidence?: Record<string, unknown>;
}) {
  return callSecureFunction<ModerationAttestResponse>('moderationAttest', payload);
}

export function mintStreamKey(payload: { streamId: string }) {
  return callSecureFunction<{ ingestToken: string; ingestUrl: string; expiresAt: number; claims: Record<string, unknown> }>('streamKeyMint', payload);
}

export function walletOperation(payload: {
  op: 'credit' | 'debit' | 'transfer';
  amount: number;
  currency?: string;
  targetUserId?: string;
  memo?: string;
  metadata?: Record<string, unknown>;
}) {
  return callSecureFunction<{ ok: boolean; result: Record<string, unknown> }>('walletSecureOp', payload);
}

export function runAdminTool(payload: { action: string; params: Record<string, unknown> }) {
  return callSecureFunction<{ ok: boolean }>('adminTool', payload);
}

export function getBrandingConfig(kind: 'draft' | 'published' = 'published') {
  return kind === 'draft'
    ? callSecureApi<{ ok: true; config: any | null }>('site/branding/draft')
    : callPublicApi<{ ok: true; config: any | null }>('site/branding');
}

export function setBrandingConfig(kind: 'draft' | 'published', payload: any) {
  return callSecureJson<{ ok: true; id: string }>(`site/branding?kind=${encodeURIComponent(kind)}`, payload, { method: 'PUT' });
}

export function getSeoConfig(kind: 'draft' | 'published' = 'published') {
  return kind === 'draft'
    ? callSecureApi<{ ok: true; config: any | null }>('site/seo/draft')
    : callPublicApi<{ ok: true; config: any | null }>('site/seo');
}

export function setSeoConfig(kind: 'draft' | 'published', payload: any) {
  return callSecureJson<{ ok: true; id: string }>(`site/seo?kind=${encodeURIComponent(kind)}`, payload, { method: 'PUT' });
}

export function queueSitemapJob() {
  return callSecureJson<{ ok: true }>('site/seo/sitemap', {});
}

export function submitContactMessage(payload: { name: string; email: string; subject: string; message: string }) {
  return callPublicApi<{ ok: true }>('site/contact', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export type WalletTopupInitResponse = {
  ok: true;
  intentId: string;
  provider: 'paypal';
  paypal?: { customId: string; amount: string; currency: 'USD' };
};

export type WalletTopupPaymentStatus = {
  intentId: string;
  status: 'created' | 'pending' | 'completed' | 'failed' | 'cancelled';
  amountUsd: number;
  currency: string;
  orderId: string | null;
  reason: string | null;
  creditedAt: string | null;
  balanceUsd: number | null;
};

export type PaymentsConfig = {
  ok: true;
  provider: 'paypal';
  paypalConfigured: boolean;
  clientId: string | null;
  env: string;
  currency: string;
};

export function getPaymentsConfig() {
  return callSecureApi<PaymentsConfig>('payments/config');
}

export function walletTopupInit(payload: { amountUsd: number }) {
  return callSecureFunction<WalletTopupInitResponse>('payments/walletTopup/init', { provider: 'paypal', ...payload });
}

export function createWalletTopupPayPalOrder(payload: { intentId: string }) {
  return callSecureFunction<{ ok: true; orderId: string }>('payments/walletTopup/paypal/order', payload);
}

export function confirmWalletTopupPayPal(payload: { orderId: string }) {
  return callSecureFunction<{ ok: boolean; payment: WalletTopupPaymentStatus | null }>('payments/walletTopup/confirm/paypal', payload);
}

export function cancelWalletTopup(payload: { intentId: string; reason?: 'cancelled' | 'error' }) {
  return callSecureFunction<{ ok: true; payment: WalletTopupPaymentStatus | null }>('payments/walletTopup/cancel', payload);
}

export function getWalletTopupStatus(intentId: string) {
  return callSecureApi<{ ok: true; payment: WalletTopupPaymentStatus }>(`payments/walletTopup/status/${encodeURIComponent(intentId)}`);
}
export type WalletMeResponse = {
  ok: true;
  account: {
    userId: string;
    currency: string;
    balanceMinor: string;
    updatedAt: string;
  };
};

export function getWalletMe() {
  return callSecureApi<WalletMeResponse>('wallet/me');
}

export type WalletTxListResponse = {
  ok: true;
  nextCursor: string | null;
  transactions: Array<{
    id: string;
    type: 'credit' | 'debit' | 'transfer';
    direction: 'in' | 'out' | 'self';
    actorUid: string;
    targetUid: string | null;
    currency: string;
    amountMinor: string;
    balanceAfterMinor: string | null;
    memo: string | null;
    metadata: any;
    createdAt: string;
  }>;
};

export function listWalletTransactions(params?: { limit?: number; cursor?: string | null }) {
  const limit = params?.limit ? Math.max(1, Math.min(100, Math.floor(params.limit))) : undefined;
  const cursor = params?.cursor ? String(params.cursor) : undefined;
  const qs = new URLSearchParams();
  if (limit) qs.set('limit', String(limit));
  if (cursor) qs.set('cursor', cursor);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return callSecureApi<WalletTxListResponse>(`wallet/transactions${suffix}`);
}

export type AdsListResponse = {
  ok: true;
  ads: any[];
};

export function listMyAds() {
  return callSecureApi<AdsListResponse>('ads/mine');
}

export function getAdById(id: string) {
  return callSecureApi<{ ok: true; ad: any }>(`ads/${encodeURIComponent(id)}`);
}

export function createAd(payload: {
  title: string;
  description: string;
  imageURL?: string;
  videoURL?: string;
  actionType?: string;
  actionValue?: string;
  buttonText?: string;
  targetURL?: string;
  pageId?: string;
  targetInterests?: string[];
  targetGender?: string;
  targetAgeMin?: number;
  targetAgeMax?: number;
  targetLocations?: any;
  placements: string[];
  budgetUsd: number;
  reviewStatus?: string;
  aiScan?: any;
  moderationTokens?: any;
}) {
  return callSecureJson<{ ok: true; id: string; status?: string; reviewStatus?: string | null }>('ads/create', payload);
}

export function updateAd(id: string, payload: Record<string, unknown>) {
  return callSecureJson<{ ok: true; id: string }>(`ads/${encodeURIComponent(id)}`, payload, { method: 'PATCH' });
}

export function deleteAd(id: string) {
  return callSecureJson<{ ok: true }>(`ads/${encodeURIComponent(id)}/delete`, {}, { method: 'POST' });
}

export type ServeAdResponse = {
  ok: true;
  ad: any | null;
};

export type NotificationsListResponse = {
  ok: true;
  notifications: Array<{
    id: string;
    type: 'like' | 'comment' | 'follow' | 'mention';
    fromUserId: string;
    fromUserName: string;
    fromUserPhoto: string;
    fromUserVerified?: boolean;
    message: string;
    snippet?: string | null;
    contentId?: string | null;
    contentType?: 'post' | 'pulse' | 'testimony' | null;
    read: boolean;
    createdAt: string;
  }>;
};

export function listNotifications(params?: { limit?: number; unreadOnly?: boolean }) {
  const qs = new URLSearchParams();
  if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
  if (typeof params?.unreadOnly === 'boolean') qs.set('unreadOnly', String(params.unreadOnly));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return callSecureApi<NotificationsListResponse>(`notifications${suffix}`);
}

export function markNotificationRead(id: string, read = true) {
  return callSecureApi<{ ok: true }>(`notifications/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ read }),
  });
}

export function createNotification(payload: {
  userId: string;
  type: 'like' | 'comment' | 'follow' | 'mention';
  fromUserName: string;
  fromUserPhoto: string;
  message: string;
  snippet?: string;
  contentId?: string | null;
  contentType?: 'post' | 'pulse' | 'testimony' | null;
}) {
  return callSecureJson<{ ok: true; id: string | null }>('notifications', payload);
}

export function ensurePublishedBrandingAdmin() {
  return callSecureJson<{ ok: true }>('site/branding/ensure', {});
}

export function serveAd(payload: { placement: string; contentType?: 'post' | 'pulse' | null; contentId?: string | null }) {
  return callSecureJson<ServeAdResponse>('ads/serve', payload);
}

export type TrackAdResponse = {
  ok: true;
  result:
    | { success: true; newBalanceMinor: string; adPaused: boolean; adCompleted: boolean }
    | { success: false; reason: string; adPaused: boolean; adCompleted: boolean };
  clickId?: string | null;
};

export function reportAdEngagement(adId: string, payload: { watchedMs: number; completed?: boolean }) {
  return callSecureJson<{ ok: true; updated: number }>(`ads/${encodeURIComponent(adId)}/engagement`, payload);
}

export function reportWatchTime(payload: { contentType: 'post' | 'pulse' | 'testimony'; contentId: string; seconds: number }) {
  return callSecureJson<{ ok: true; credited: number }>('earnings/watch', payload);
}

export function trackAdImpression(adId: string, payload?: { placement?: string; contentId?: string; contentType?: 'post' | 'pulse' }) {
  return callSecureJson<TrackAdResponse>(`ads/${encodeURIComponent(adId)}/impression`, payload || {});
}

export function trackAdClick(adId: string, payload?: { placement?: string; contentId?: string; contentType?: 'post' | 'pulse' }) {
  return callSecureJson<TrackAdResponse>(`ads/${encodeURIComponent(adId)}/click`, payload || {});
}

export type PostDto = {
  id: string;
  userId: string;
  userName: string;
  username?: string | null;
  userPhoto: string;
  userVerified?: boolean;
  content: string;
  imageURL?: string | null;
  videoURL?: string | null;
  bgMusic?: any;
  likeCount: number;
  likedByMe: boolean;
  comments: number;
  audience?: string;
  ownerType?: string | null;
  pageId?: string | null;
  pageName?: string | null;
  pagePhoto?: string | null;
  type?: string | null;
  repostOf?: string | null;
  original?: any;
  originalStreamId?: string | null;
  streamThumbnail?: string | null;
  streamRefPath?: string | null;
  title?: string | null;
  moderation?: any;
  hashtags?: string[];
  mentions?: string[];
  mediaPath?: string | null;
  createdAt: string;
};

export function getPostById(id: string) {
  return callSecureApi<{ ok: true; post: PostDto }>(`posts/${encodeURIComponent(id)}`);
}

export function listPosts(params?: { limit?: number; hashtag?: string; userId?: string }) {
  const qs = new URLSearchParams();
  if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
  if (params?.hashtag) qs.set('hashtag', params.hashtag);
  if (params?.userId) qs.set('userId', params.userId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return callSecureApi<{ ok: true; posts: PostDto[] }>(`posts${suffix}`);
}

export function createPost(payload: Record<string, unknown>) {
  return callSecureJson<{ ok: true; id: string; post: PostDto }>('posts', payload);
}

export function updatePost(id: string, payload: { content: string }) {
  return callSecureApi<{ ok: true }>(`posts/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function deletePost(id: string) {
  return callSecureApi<{ ok: true }>(`posts/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function likePost(id: string) {
  return callSecureJson<{ ok: true }>(`posts/${encodeURIComponent(id)}/like`, {});
}

export function unlikePost(id: string) {
  return callSecureJson<{ ok: true }>(`posts/${encodeURIComponent(id)}/unlike`, {});
}

export type CommentDto = {
  id: string;
  userId: string;
  userName: string;
  userPhoto: string;
  userVerified?: boolean;
  content: string;
  parentId?: string | null;
  likeCount: number;
  likedByMe: boolean;
  createdAt: string;
  updatedAt: string;
};

export function listComments(params: { contentType: 'post' | 'pulse' | 'testimony'; contentId: string; limit?: number }) {
  const qs = new URLSearchParams();
  qs.set('contentType', params.contentType);
  qs.set('contentId', params.contentId);
  if (typeof params.limit === 'number') qs.set('limit', String(params.limit));
  return callSecureApi<{ ok: true; comments: CommentDto[] }>(`comments?${qs.toString()}`);
}

export function createComment(payload: {
  contentType: 'post' | 'pulse' | 'testimony';
  contentId: string;
  content: string;
  parentId?: string | null;
  userName?: string;
  userPhoto?: string;
}) {
  return callSecureJson<{ ok: true; id: string; comment: CommentDto }>('comments', payload);
}

export function updateComment(id: string, payload: { content: string }) {
  return callSecureApi<{ ok: true }>(`comments/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function deleteComment(id: string) {
  return callSecureApi<{ ok: true; deleted: number }>(`comments/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function likeComment(id: string) {
  return callSecureJson<{ ok: true }>(`comments/${encodeURIComponent(id)}/like`, {});
}

export function unlikeComment(id: string) {
  return callSecureJson<{ ok: true }>(`comments/${encodeURIComponent(id)}/unlike`, {});
}

export type UserProfileDto = {
  uid: string;
  name: string;
  username?: string | null;
  email?: string | null;
  role?: string | null;
  bio?: string | null;
  photoURL?: string | null;
  coverPhotoURL?: string | null;
  verified?: boolean;
  dateOfBirth?: string | null;
  gender?: string | null;
  location?: string | null;
  detectedLocation?: any;
  interests?: string[];
  website?: string | null;
  socialLinks?: any;
  profilePrivacy?: any;
  followers?: number;
  following?: number;
  followedByMe?: boolean;
  creatorScore?: number | null;
  usernameLastChangedAt?: string | null;
};

export function getUserMe() {
  return callSecureApi<{ ok: true; profile: UserProfileDto }>('users/me');
}

export function getUserById(id: string) {
  return callSecureApi<{ ok: true; profile: UserProfileDto }>(`users/${encodeURIComponent(id)}`);
}

export function getUserByUsername(username: string) {
  return callSecureApi<{ ok: true; profile: UserProfileDto }>(`users/by-username/${encodeURIComponent(username)}`);
}

export const PROFILE_UPDATED_EVENT = 'belivah:profile-updated';

export async function updateUserProfile(payload: Partial<UserProfileDto> & { moderationTokens?: Record<string, unknown> | null }) {
  const res = await callSecureApi<{ ok: true; profile: UserProfileDto }>('users/me', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  // Lets the navigation bars refresh the avatar/name without a page reload.
  try { window.dispatchEvent(new CustomEvent(PROFILE_UPDATED_EVENT, { detail: res.profile })); } catch {}
  return res;
}

export function followUser(userId: string) {
  return callSecureJson<{ ok: true }>(`users/${encodeURIComponent(userId)}/follow`, {});
}

export function unfollowUser(userId: string) {
  return callSecureJson<{ ok: true }>(`users/${encodeURIComponent(userId)}/unfollow`, {});
}

export function listFollowers(userId: string, params?: { limit?: number }) {
  const qs = new URLSearchParams();
  if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return callSecureApi<{ ok: true; users: Array<{ id: string; name: string; username?: string; photoURL?: string }> }>(
    `users/${encodeURIComponent(userId)}/followers${suffix}`,
  );
}

export function listFollowing(userId: string, params?: { limit?: number }) {
  const qs = new URLSearchParams();
  if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return callSecureApi<{ ok: true; users: Array<{ id: string; name: string; username?: string; photoURL?: string }> }>(
    `users/${encodeURIComponent(userId)}/following${suffix}`,
  );
}

export async function listUsersByIds(ids: string[]) {
  type UserLite = { uid: string; name: string; username?: string; photoURL?: string };
  const unique = Array.from(new Set((ids || []).filter((id) => typeof id === 'string' && id.length > 0)));
  // Backend caps each request at 50 ids.
  const users: UserLite[] = [];
  for (let i = 0; i < unique.length; i += 50) {
    const res = await callSecureJson<{ ok: true; users: UserLite[] }>('users/by-ids', { ids: unique.slice(i, i + 50) });
    users.push(...(res.users || []));
  }
  return { ok: true as const, users };
}

export function listTopCreators(params?: { limit?: number }) {
  const qs = new URLSearchParams();
  if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return callSecureApi<{ ok: true; users: Array<{ uid: string; name: string; username?: string; photoURL?: string; creatorScore?: number; followers?: number }> }>(
    `users/top${suffix}`,
  );
}

export function listSuggestedUsers(params?: { limit?: number }) {
  const qs = new URLSearchParams();
  if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return callSecureApi<{ ok: true; users: Array<{ uid: string; name: string; username?: string; photoURL?: string }> }>(
    `users/suggested${suffix}`,
  );
}

export function getUserStats() {
  return callSecureApi<{ ok: true; stats: { posts: number; followers: number; following: number } }>('users/me/stats');
}

export type PulseDto = {
  id: string;
  userId: string;
  userName: string;
  userPhoto: string;
  userVerified?: boolean;
  videoURL: string;
  thumbnailURL?: string | null;
  caption: string;
  category: string;
  audience?: string;
  bgMusic?: any;
  ownerType?: string | null;
  pageId?: string | null;
  pageName?: string | null;
  pagePhoto?: string | null;
  type?: string | null;
  repostOf?: string | null;
  original?: any;
  moderation?: any;
  hashtags?: string[];
  mentions?: string[];
  mediaPath?: string | null;
  likeCount: number;
  likedByMe: boolean;
  comments: number;
  views: number;
  createdAt: string;
};

export function listPulse(params?: { limit?: number; category?: string; mode?: 'forYou' | 'my' | 'category' | 'trending'; userId?: string }) {
  const qs = new URLSearchParams();
  if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
  if (params?.category) qs.set('category', params.category);
  if (params?.mode) qs.set('mode', params.mode);
  if (params?.userId) qs.set('userId', params.userId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return callSecureApi<{ ok: true; pulses: PulseDto[] }>(`pulse${suffix}`);
}

export function getPulseById(id: string) {
  return callSecureApi<{ ok: true; pulse: PulseDto }>(`pulse/${encodeURIComponent(id)}`);
}

export function createPulse(payload: Record<string, unknown>) {
  return callSecureJson<{ ok: true; id: string; pulse: PulseDto }>('pulse', payload);
}

export function updatePulse(id: string, payload: Record<string, unknown>) {
  return callSecureApi<{ ok: true }>(`pulse/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function deletePulse(id: string) {
  return callSecureApi<{ ok: true }>(`pulse/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function likePulse(id: string) {
  return callSecureJson<{ ok: true }>(`pulse/${encodeURIComponent(id)}/like`, {});
}

export function unlikePulse(id: string) {
  return callSecureJson<{ ok: true }>(`pulse/${encodeURIComponent(id)}/unlike`, {});
}

export function viewPulse(id: string) {
  return callSecureJson<{ ok: true }>(`pulse/${encodeURIComponent(id)}/view`, {});
}

export type TestimonyDto = {
  id: string;
  userId: string;
  userName: string;
  userPhoto: string;
  userVerified?: boolean;
  title: string;
  content: string;
  imageURL?: string | null;
  videoURL?: string | null;
  bgMusic?: any;
  audience?: string;
  featured?: boolean;
  type?: string | null;
  repostOf?: string | null;
  original?: any;
  moderation?: any;
  hashtags?: string[];
  mentions?: string[];
  mediaPath?: string | null;
  likeCount: number;
  likedByMe: boolean;
  comments: number;
  views: number;
  createdAt: string;
};

export function listTestimonies(params?: { limit?: number; mode?: 'forYou' | 'mine' | 'trending' }) {
  const qs = new URLSearchParams();
  if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
  if (params?.mode) qs.set('mode', params.mode);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return callSecureApi<{ ok: true; testimonies: TestimonyDto[] }>(`testimonies${suffix}`);
}

export function getTestimonyById(id: string) {
  return callSecureApi<{ ok: true; testimony: TestimonyDto }>(`testimonies/${encodeURIComponent(id)}`);
}

export function createTestimony(payload: Record<string, unknown>) {
  return callSecureJson<{ ok: true; id: string; testimony: TestimonyDto }>('testimonies', payload);
}

export function updateTestimony(id: string, payload: Record<string, unknown>) {
  return callSecureApi<{ ok: true }>(`testimonies/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function deleteTestimony(id: string) {
  return callSecureApi<{ ok: true }>(`testimonies/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function likeTestimony(id: string) {
  return callSecureJson<{ ok: true }>(`testimonies/${encodeURIComponent(id)}/like`, {});
}

export function unlikeTestimony(id: string) {
  return callSecureJson<{ ok: true }>(`testimonies/${encodeURIComponent(id)}/unlike`, {});
}

export function viewTestimony(id: string) {
  return callSecureJson<{ ok: true }>(`testimonies/${encodeURIComponent(id)}/view`, {});
}

export type StoryDto = {
  id: string;
  userId: string;
  userName: string;
  userPhoto: string;
  mediaURL: string;
  mediaType: 'image' | 'video';
  createdAt: string;
  expiresAt: string;
};

export function listStories(params?: { limit?: number }) {
  const qs = new URLSearchParams();
  if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return callSecureApi<{ ok: true; stories: StoryDto[] }>(`stories${suffix}`);
}

export function createStory(payload: Record<string, unknown>) {
  return callSecureJson<{ ok: true; id: string; story: StoryDto }>('stories', payload);
}

export function deleteStory(id: string) {
  return callSecureApi<{ ok: true }>(`stories/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export type SavedItemDto = {
  id: string;
  contentType: 'post' | 'pulse' | 'testimony' | 'story';
  contentId: string;
  createdAt: string;
};

export function listSaved(params?: { limit?: number; contentType?: 'post' | 'pulse' | 'testimony' | 'story' }) {
  const qs = new URLSearchParams();
  if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
  if (params?.contentType) qs.set('contentType', params.contentType);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return callSecureApi<{ ok: true; saved: SavedItemDto[] }>(`saved${suffix}`);
}

export function saveItem(payload: { contentType: 'post' | 'pulse' | 'testimony' | 'story'; contentId: string }) {
  return callSecureJson<{ ok: true }>('saved', payload);
}

export function unsaveItem(contentType: 'post' | 'pulse' | 'testimony' | 'story', contentId: string) {
  return callSecureApi<{ ok: true }>(`saved/${encodeURIComponent(contentType)}/${encodeURIComponent(contentId)}`, { method: 'DELETE' });
}

export type SettingsResponse = {
  ok: true;
  settings: {
    whoCanSeeMyPosts: 'everyone' | 'followers' | 'nobody';
    whoCanMessage: 'everyone' | 'followers' | 'nobody';
    notifyOnLikes: boolean;
    notifyOnComments: boolean;
    notifyOnFollows: boolean;
    notifyOnMentions: boolean;
    blockedUsers: string[];
    defaultAudience: 'public' | 'friends';
  };
  userData: {
    followers: number;
    verified: boolean;
    verificationApplicationDate?: string | null;
    verificationStatus?: 'pending' | 'approved' | 'rejected' | null;
  };
};

export function getSettings() {
  return callSecureApi<SettingsResponse>('settings');
}

export function updateSettings(payload: Partial<SettingsResponse['settings']>) {
  return callSecureApi<{ ok: true }>('settings', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export type SearchResponse = {
  ok: true;
  users: Array<{ id: string; name: string; username?: string | null; photoURL?: string | null; verified?: boolean }>;
  groups?: Array<{ id: string; name: string; description: string; category: string; isPrivate: boolean; coverImage?: string | null; memberCount: number }>;
  posts: Array<{ id: string; userId: string; userName: string; userPhoto: string; content: string; imageURL?: string | null; videoURL?: string | null; createdAt: string }>;
  pulses: Array<{ id: string; userId: string; userName: string; userPhoto: string; caption: string; videoURL: string; createdAt: string }>;
  testimonies: Array<{ id: string; userId: string; userName: string; userPhoto: string; title: string; content: string; imageURL?: string | null; videoURL?: string | null; createdAt: string }>;
};

export function searchAll(query: string, limit?: number) {
  const qs = new URLSearchParams();
  qs.set('q', query);
  if (typeof limit === 'number') qs.set('limit', String(limit));
  return callSecureApi<SearchResponse>(`search?${qs.toString()}`);
}

export function createReport(payload: { contentId: string; contentType: string; reportedUserId: string; reason: string; additionalInfo?: string }) {
  return callSecureJson<{ ok: true }>('reports', payload);
}

export type MusicTrackDto = {
  id: string;
  title: string;
  artistName: string;
  artistId: string;
  audioURL: string;
  coverArtURL?: string;
  isPublic: boolean;
  allowReels: boolean;
  status: string;
  reelsUses: number;
  testimoniesUses: number;
  createdAt: string;
  updatedAt: string;
};

export function listMusicTracks(params?: { isPublic?: boolean; allowReels?: boolean; status?: string; artistId?: string; limit?: number }) {
  const qs = new URLSearchParams();
  if (typeof params?.isPublic === 'boolean') qs.set('isPublic', String(params.isPublic));
  if (typeof params?.allowReels === 'boolean') qs.set('allowReels', String(params.allowReels));
  if (params?.status) qs.set('status', params.status);
  if (params?.artistId) qs.set('artistId', String(params.artistId));
  if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return callSecureApi<{ ok: true; tracks: MusicTrackDto[] }>(`music/tracks${suffix}`);
}

export function createMusicTrack(payload: { title: string; artistName: string; artistId: string; audioURL: string; coverArtURL?: string; moderationTokens?: any; isPublic?: boolean; allowReels?: boolean }) {
  return callSecureJson<{ ok: true; id: string; track: MusicTrackDto }>('music/tracks', payload);
}

export function updateMusicTrack(id: string, payload: Partial<MusicTrackDto>) {
  return callSecureApi<{ ok: true }>(`music/tracks/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function deleteMusicTrack(id: string) {
  return callSecureApi<{ ok: true }>(`music/tracks/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function getArtistVerification() {
  return callSecureApi<{
    ok: true;
    verification: any | null;
    followers?: number;
    profileVerified?: boolean;
    eligible?: { public_figure: boolean };
    requiredDocuments?: Record<string, string[]>;
  }>('artist-verification/me');
}

export function submitArtistVerification(payload: { fullName: string; verificationType: 'creator' | 'artist' | 'public_figure'; documents: any[]; bankDetails?: any; moderationTokens?: any }) {
  return callSecureJson<{ ok: true; verification: any }>('artist-verification', payload);
}

export function listMyPages() {
  return callSecureApi<{ ok: true; pages: any[] }>('pages/mine');
}

export function listFollowedPages() {
  return callSecureApi<{ ok: true; pages: any[] }>('pages/following');
}

export function getPageById(id: string) {
  return callSecureApi<{ ok: true; page: any }>(`pages/${encodeURIComponent(id)}`);
}

export function createPage(payload: any) {
  return callSecureJson<{ ok: true; page: any }>('pages', payload);
}

export function updatePage(id: string, payload: any) {
  return callSecureJson<{ ok: true; page: any }>(`pages/${encodeURIComponent(id)}`, payload, { method: 'PATCH' });
}

export function deletePage(id: string) {
  return callSecureApi<{ ok: true }>(`pages/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function followPage(id: string, follow?: boolean) {
  return callSecureJson<{ ok: true; page: any }>(`pages/${encodeURIComponent(id)}/follow`, { follow });
}

export function updatePageTeam(id: string, payload: { action: 'add' | 'remove'; role: 'admin' | 'editor'; userId: string }) {
  return callSecureJson<{ ok: true; page: any }>(`pages/${encodeURIComponent(id)}/team`, payload);
}

export function listGroups(params?: { sort?: 'trending' | 'new'; limit?: number }) {
  const qs = new URLSearchParams();
  if (params?.sort) qs.set('sort', params.sort);
  if (params?.limit) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return callSecureApi<{ ok: true; groups: any[] }>(`groups${suffix}`);
}

export function listMyGroups() {
  return callSecureApi<{ ok: true; groups: any[] }>('groups/mine');
}

export function getGroupById(id: string) {
  return callSecureApi<{ ok: true; group: any }>(`groups/${encodeURIComponent(id)}`);
}

export function createGroup(payload: { name: string; description: string; category: string; isPrivate?: boolean; coverImage?: string | null; adminName: string }) {
  return callSecureJson<{ ok: true; group: any }>('groups', payload);
}

export function joinGroup(id: string) {
  return callSecureJson<{ ok: true; group: any }>(`groups/${encodeURIComponent(id)}/join`, {});
}

export function leaveGroup(id: string) {
  return callSecureJson<{ ok: true; group: any }>(`groups/${encodeURIComponent(id)}/leave`, {});
}

export function updateGroup(id: string, payload: { name?: string; description?: string; category?: string; isPrivate?: boolean; coverImage?: string | null }) {
  return callSecureJson<{ ok: true; group: any }>(`groups/${encodeURIComponent(id)}`, payload, { method: 'PATCH' });
}

export function deleteGroup(id: string) {
  return callSecureApi<{ ok: true }>(`groups/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export type GroupRole = 'owner' | 'admin' | 'moderator' | 'member';

export function getGroupTeam(id: string) {
  return callSecureApi<{ ok: true; myRole: GroupRole | null; members: { uid: string; name: string; username: string; photoURL: string; role: GroupRole }[] }>(`groups/${encodeURIComponent(id)}/team`);
}

export function manageGroupTeam(id: string, payload: { action: 'add' | 'remove'; role: 'admin' | 'moderator' | 'member'; userId: string }) {
  return callSecureJson<{ ok: true }>(`groups/${encodeURIComponent(id)}/team`, payload);
}

export function deleteGroupPost(postId: string) {
  return callSecureApi<{ ok: true }>(`groups/posts/${encodeURIComponent(postId)}`, { method: 'DELETE' });
}

export function updateGroupThread(threadId: string, payload: { title?: string; body?: string; pinned?: boolean; locked?: boolean }) {
  return callSecureJson<{ ok: true }>(`groups/threads/${encodeURIComponent(threadId)}`, payload, { method: 'PATCH' });
}

export function deleteGroupThread(threadId: string) {
  return callSecureApi<{ ok: true }>(`groups/threads/${encodeURIComponent(threadId)}`, { method: 'DELETE' });
}

export function listGroupPosts(groupId: string) {  return callSecureApi<{ ok: true; posts: any[] }>(`groups/${encodeURIComponent(groupId)}/posts`);
}

export function listMyGroupPosts() {
  return callSecureApi<{ ok: true; posts: any[] }>('groups/posts/mine');
}

export function createGroupPost(groupId: string, payload: { content: string; imageURL?: string | null; userName: string; userPhoto?: string | null }) {
  return callSecureJson<{ ok: true; post: any }>(`groups/${encodeURIComponent(groupId)}/posts`, payload);
}

export function toggleGroupPostLike(postId: string) {
  return callSecureJson<{ ok: true; post: any }>(`groups/posts/${encodeURIComponent(postId)}/like`, {});
}

export function listGroupThreads(groupId: string, view?: 'trending' | 'new' | 'active') {
  const qs = view ? `?view=${encodeURIComponent(view)}` : '';
  return callSecureApi<{ ok: true; threads: any[] }>(`groups/${encodeURIComponent(groupId)}/threads${qs}`);
}

export function listGroupThreadsGlobal(view?: 'trending' | 'new' | 'active' | 'mine') {
  const qs = view ? `?view=${encodeURIComponent(view)}` : '';
  return callSecureApi<{ ok: true; threads: any[] }>(`groups/threads${qs}`);
}

export function createGroupThread(payload: { groupId: string; groupName?: string | null; groupCategory?: string | null; title: string; body: string; userName: string; userPhoto?: string | null }) {
  return callSecureJson<{ ok: true; thread: any }>('groups/threads', payload);
}

export function listGroupReplies(threadId: string) {
  return callSecureApi<{ ok: true; replies: any[] }>(`groups/threads/${encodeURIComponent(threadId)}/replies`);
}

export function createGroupReply(threadId: string, payload: { groupId: string; body: string; parentId?: string | null; userName: string; userPhoto?: string | null }) {
  return callSecureJson<{ ok: true; reply: any }>(`groups/threads/${encodeURIComponent(threadId)}/replies`, payload);
}

export function toggleGroupThreadLike(threadId: string) {
  return callSecureJson<{ ok: true; thread: any }>(`groups/threads/${encodeURIComponent(threadId)}/like`, {});
}

export function listGroupPostComments(postId: string, parentId?: string | null) {
  const qs = parentId ? `?parentId=${encodeURIComponent(parentId)}` : '';
  return callSecureApi<{ ok: true; comments: any[] }>(`groups/posts/${encodeURIComponent(postId)}/comments${qs}`);
}

export function createGroupPostComment(postId: string, payload: { content: string; parentId?: string | null; userName: string; userPhoto?: string | null }) {
  return callSecureJson<{ ok: true; comment: any }>(`groups/posts/${encodeURIComponent(postId)}/comments`, payload);
}

export function toggleGroupPostCommentLike(commentId: string) {
  return callSecureJson<{ ok: true; comment: any }>(`groups/posts/comments/${encodeURIComponent(commentId)}/like`, {});
}

export function listConversations() {
  return callSecureApi<{ ok: true; conversations: any[] }>('messaging/conversations');
}

export function getUnreadMessageCount() {
  return callSecureApi<{ ok: true; total: number; conversations: number }>('messaging/unread');
}

export type PresenceInfo = { online: boolean; lastSeen: string | null };
export function getPresence(ids: string[]) {
  return callSecureApi<{ ok: true; presence: Record<string, PresenceInfo> }>(`messaging/presence?ids=${encodeURIComponent(ids.join(','))}`);
}

export function createConversation(payload: { participantId: string; participantName: string; participantPhoto?: string | null; senderName: string; senderPhoto?: string | null }) {
  return callSecureJson<{ ok: true; conversation: any }>('messaging/conversations', payload);
}

export type FriendStatus = 'self' | 'none' | 'outgoing' | 'incoming' | 'friends';
export type FriendUser = { uid: string; name: string | null; username?: string | null; photoURL?: string | null };

export function getFriendStatus(userId: string) {
  return callSecureApi<{ ok: true; status: FriendStatus; requestId: string | null }>(`friends/status/${encodeURIComponent(userId)}`);
}

export function sendFriendRequest(userId: string) {
  return callSecureJson<{ ok: true; status: FriendStatus }>('friends/request', { userId });
}

export function acceptFriendRequest(requestId: string) {
  return callSecureJson<{ ok: true; status: FriendStatus }>(`friends/requests/${encodeURIComponent(requestId)}/accept`, {});
}

export function declineFriendRequest(requestId: string) {
  return callSecureJson<{ ok: true; status: FriendStatus }>(`friends/requests/${encodeURIComponent(requestId)}/decline`, {});
}

export function removeFriend(userId: string) {
  return callSecureApi<{ ok: true; status: FriendStatus }>(`friends/${encodeURIComponent(userId)}`, { method: 'DELETE' });
}

export function listFriendRequests() {
  return callSecureApi<{ ok: true; requests: Array<{ id: string; createdAt: string; user: FriendUser }> }>('friends/requests');
}

export function listFriends() {
  return callSecureApi<{ ok: true; friends: FriendUser[] }>('friends');
}

export function listMessages(conversationId: string) {
  return callSecureApi<{ ok: true; messages: any[] }>(`messaging/conversations/${encodeURIComponent(conversationId)}/messages`);
}

export type ChatAttachmentInput = {
  url: string;
  type: 'image' | 'video' | 'audio' | 'file';
  name?: string | null;
  size?: number | null;
  durationSec?: number | null;
};

export function sendMessage(conversationId: string, payload: { content: string; senderName: string; attachment?: ChatAttachmentInput | null; moderationTokens?: Record<string, unknown> | null }) {
  return callSecureJson<{ ok: true; message: any }>(`messaging/conversations/${encodeURIComponent(conversationId)}/messages`, payload);
}

export type CallSignalKind = 'offer' | 'answer' | 'decline' | 'busy' | 'end' | 'cancel';

export function getCallIceServers() {
  return callSecureApi<{ ok: true; iceServers: RTCIceServer[] }>('messaging/calls/ice-servers');
}

export function sendCallSignal(payload: { toUserId: string; callId: string; kind: CallSignalKind; sdp?: string | null; video?: boolean; durationSec?: number | null }) {
  return callSecureJson<{ ok: true }>('messaging/calls/signal', payload);
}

export function searchUsers(payload: { q: string; limit?: number }) {
  const qs = new URLSearchParams();
  qs.set('q', payload.q);
  if (payload.limit) qs.set('limit', String(payload.limit));
  return callSecureApi<{ ok: true; users: any[] }>(`messaging/users/search?${qs.toString()}`);
}

export function listLiveStreams() {
  return callSecureApi<{ ok: true; streams: any[] }>('live');
}

export function listMyLiveStreams() {
  return callSecureApi<{ ok: true; streams: any[] }>('live/mine');
}

export function getLiveStream(id: string) {
  return callSecureApi<{ ok: true; stream: any }>(`live/${encodeURIComponent(id)}`);
}

export function createLiveStream(payload: any) {
  return callSecureJson<{ ok: true; stream: any }>('live', payload);
}

export function updateLiveStream(id: string, payload: any) {
  return callSecureJson<{ ok: true; stream: any }>(`live/${encodeURIComponent(id)}`, payload, { method: 'PATCH' });
}

export function endLiveStream(id: string) {
  return callSecureJson<{ ok: true; stream: any }>(`live/${encodeURIComponent(id)}/end`, {});
}

export function deleteLiveStream(id: string) {
  return callSecureApi<{ ok: true }>(`live/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function listLiveMessages(streamId: string) {
  return callSecureApi<{ ok: true; messages: any[] }>(`live/${encodeURIComponent(streamId)}/messages`);
}

export function sendLiveMessage(streamId: string, payload: { text: string; userName: string; userPhoto?: string | null }) {
  return callSecureJson<{ ok: true; message: any }>(`live/${encodeURIComponent(streamId)}/messages`, payload);
}

export function likeLiveStream(streamId: string) {
  return callSecureJson<{ ok: true; likesCount: number }>(`live/${encodeURIComponent(streamId)}/like`, {});
}

export function unlikeLiveStream(streamId: string) {
  return callSecureJson<{ ok: true; likesCount: number }>(`live/${encodeURIComponent(streamId)}/unlike`, {});
}

export function heartbeatLivePresence(streamId: string) {
  return callSecureJson<{ ok: true; presenceCount: number }>(`live/${encodeURIComponent(streamId)}/presence`, {});
}

export function listReports(status?: 'pending' | 'all' | 'reviewed' | 'resolved' | 'dismissed') {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return callSecureApi<{ ok: true; reports: any[] }>(`admin/reports${qs}`);
}

export function updateReportStatus(id: string, status: 'pending' | 'reviewed' | 'resolved' | 'dismissed') {
  return callSecureJson<{ ok: true; report: any }>(`admin/reports/${encodeURIComponent(id)}`, { status }, { method: 'PATCH' });
}

export function deleteReportContent(id: string) {
  return callSecureJson<{ ok: true }>(`admin/reports/${encodeURIComponent(id)}/delete-content`, {});
}

export type AdminContentType = 'post' | 'pulse' | 'testimony';
export type AdminContentItem = {
  id: string;
  type: AdminContentType;
  authorId: string;
  author: string;
  text: string;
  imageURL: string | null;
  videoURL: string | null;
  likeCount: number;
  commentCount: number;
  createdAt: string;
  link: string;
};

export function listAdminContent(params: { type: AdminContentType; q?: string; before?: string; limit?: number }) {
  const qs = new URLSearchParams({ type: params.type });
  if (params.q) qs.set('q', params.q);
  if (params.before) qs.set('before', params.before);
  if (params.limit) qs.set('limit', String(params.limit));
  return callSecureApi<{ ok: true; items: AdminContentItem[]; nextBefore: string | null }>(`admin/content?${qs.toString()}`);
}

export function adminDeleteContent(type: AdminContentType, id: string, reason?: string) {
  return callSecureJson<{ ok: true }>(`admin/content/${encodeURIComponent(type)}/${encodeURIComponent(id)}/delete`, { reason: reason || null });
}

export function listWithdrawals(status?: 'pending' | 'all' | 'processing' | 'completed' | 'failed') {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return callSecureApi<{ ok: true; withdrawals: any[] }>(`admin/withdrawals${qs}`);
}

export function updateWithdrawal(id: string, payload: { status: 'pending' | 'processing' | 'completed' | 'failed'; failureReason?: string | null }) {
  return callSecureJson<{ ok: true; withdrawal: any }>(`admin/withdrawals/${encodeURIComponent(id)}`, payload, { method: 'PATCH' });
}

export function listAppeals(status?: 'pending' | 'under_review' | 'resolved') {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return callSecureApi<{ ok: true; appeals: any[] }>(`admin/appeals${qs}`);
}

export function resolveAppeal(id: string, payload: { decision: 'approved' | 'rejected'; moderatorNote?: string | null }) {
  return callSecureJson<{ ok: true; appeal: any }>(`admin/appeals/${encodeURIComponent(id)}/resolve`, payload);
}

export function getAnalyticsSummary(days: number) {
  return callSecureApi<{ ok: true; summary: any }>(`admin/analytics/summary?days=${encodeURIComponent(String(days))}`);
}

export function getAnalyticsTrends() {
  return callSecureApi<{ ok: true; trends: any[] }>('admin/analytics/trends');
}

export function getAdminStats() {
  return callSecureApi<{ ok: true; stats: any }>('admin/stats');
}

export function logAdminAudit(payload: any) {
  return callSecureJson<{ ok: true }>('admin/audit', payload);
}

export function listAdminUsers(params?: { q?: string; limit?: number }) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return callSecureApi<{ ok: true; users: any[] }>(`admin/users${suffix}`);
}

export function listAdminArtistVerifications(params?: { status?: 'pending' | 'approved' | 'rejected' | 'additional_info_required'; limit?: number }) {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return callSecureApi<{ ok: true; verifications: any[] }>(`admin/artist-verifications${suffix}`);
}

export function updateAdminArtistVerification(id: string, payload: { status: 'pending' | 'approved' | 'rejected' | 'additional_info_required'; rejectionReason?: string | null }) {
  return callSecureJson<{ ok: true; verification: any }>(`admin/artist-verifications/${encodeURIComponent(id)}`, payload, { method: 'PATCH' });
}

export function listAdminMusicTracks(params?: { status?: string; isPublic?: boolean; allowReels?: boolean; artistId?: string; limit?: number }) {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (typeof params?.isPublic === 'boolean') qs.set('isPublic', String(params.isPublic));
  if (typeof params?.allowReels === 'boolean') qs.set('allowReels', String(params.allowReels));
  if (params?.artistId) qs.set('artistId', params.artistId);
  if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return callSecureApi<{ ok: true; tracks: any[] }>(`admin/music-tracks${suffix}`);
}

export function updateAdminMusicTrack(id: string, payload: { title?: string; artistName?: string; isPublic?: boolean; allowReels?: boolean; status?: string }) {
  return callSecureJson<{ ok: true; track: any }>(`admin/music-tracks/${encodeURIComponent(id)}`, payload, { method: 'PATCH' });
}

export function deleteAdminMusicTrack(id: string) {
  return callSecureApi<{ ok: true }>(`admin/music-tracks/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function listAdminAds(params?: { status?: 'draft' | 'pending_approval' | 'active' | 'paused' | 'completed' | 'rejected'; limit?: number }) {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return callSecureApi<{ ok: true; ads: any[] }>(`admin/ads${suffix}`);
}

export function updateAdminAd(id: string, payload: { status?: 'draft' | 'pending_approval' | 'active' | 'paused' | 'completed' | 'rejected'; reviewStatus?: string | null }) {
  return callSecureJson<{ ok: true; ad: any }>(`admin/ads/${encodeURIComponent(id)}`, payload, { method: 'PATCH' });
}

export function listConversions() {
  return callSecureApi<{ ok: true; conversions: any[] }>('conversions/mine');
}

export function getConversionPixelConfig() {
  return callSecureApi<{ ok: true; enabled: boolean; advertiserId: string }>('conversions/pixel');
}

export function getEarningsMe() {
  return callSecureApi<{ ok: true; earnings: any; withdrawals: any[] }>('earnings/me');
}

export function requestWithdrawal(payload: { amount: number; currency?: string; method?: string; paypalEmail?: string; paymentDetails?: any }) {
  return callSecureJson<{ ok: true; withdrawal: any }>('earnings/withdrawals', payload);
}
